import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

import { TONE_FILL, type Tone } from "@/components/ui";

import { useControlAttrs } from "./Field";

/**
 * Input, Select and Textarea share one look — border, height, placeholder,
 * disabled and invalid states — so a row of mixed controls lines up. The focus
 * ring is the global one (`index.css`); nothing here restates it. The one
 * exception is an `Input` with an `icon` or a `suffix`: its border belongs to
 * the wrapper, so the wrapper draws the ring (`focus-ring-has`, which needs
 * `:has()`) and the bare input inside takes none.
 *
 * Each reads its id and its `aria-describedby` / `aria-invalid` from the
 * `Field` around it. Outside a Field, give it an `aria-label` (development
 * reports a control that has neither).
 *
 * Two competing utilities on one element (`bg-surface` next to `bg-sunken`,
 * `w-32` next to `w-full`) are a trap: which one wins depends on the order of
 * the generated stylesheet, not on the order written here. So each element
 * gets ONE class per property, and `className` is for what the base never sets
 * (a height). A `pill` select takes a `tone`, never a colour. **Never a width**: the base is
 * `w-full`, and in the built CSS `w-full` comes after `w-32` — set the width on
 * the `Field` (`className="w-32"`) or on a wrapper.
 */
const SIZE = { md: "px-3 py-2", sm: "px-2.5 py-1" } as const;
type Size = keyof typeof SIZE;

const TEXT =
  "text-sm text-ink placeholder:text-ink-muted disabled:cursor-not-allowed disabled:text-ink-muted";
// The bare control: its own border, and a disabled state that reads as locked.
// The border WIDTH (`border`) is what gives the field an edge: Tailwind's
// preflight resets it to 0, so a border colour alone draws nothing and the
// field would vanish into a white card. An author-set background overrides the
// browser's own tint for disabled fields, so that state is restated here.
const CONTROL =
  "block w-full rounded-md border border-line-field bg-surface shadow-xs disabled:bg-sunken aria-[invalid=true]:border-danger-dot";

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  size?: Size;
  /** Monospace, for identifiers, keys and tokens. */
  mono?: boolean;
  /** A decorative icon before the text (the glyph of a search box). */
  icon?: ReactNode;
  /** Fixed text after the value, inside the field (a domain suffix). */
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = "md", mono, icon, suffix, className = "", ...rest },
  ref,
) {
  const { attrs, invalid } = useControlAttrs(rest);
  const font = mono ? "font-mono" : "";

  if (!icon && !suffix) {
    return (
      <input
        {...rest}
        {...attrs}
        ref={ref}
        className={`${CONTROL} ${TEXT} ${SIZE[size]} ${font} ${className}`}
      />
    );
  }

  // With an adornment the border belongs to the wrapper and the bare input sits
  // inside it. The wrapper draws the ring (`focus-ring-has`), so the input
  // itself takes none: a second ring inside the first would be the very
  // duplication the global one exists to prevent.
  return (
    <div
      className={`focus-ring-has flex w-full items-center gap-2 rounded-md border shadow-xs ${SIZE[size]} ${
        invalid ? "border-danger-dot" : "border-line-field"
      } ${rest.disabled ? "bg-sunken" : "bg-surface"} ${className}`}
    >
      {icon && (
        <span
          aria-hidden="true"
          className="flex shrink-0 text-ink-muted [&>svg]:h-4 [&>svg]:w-4"
        >
          {icon}
        </span>
      )}
      <input
        {...rest}
        {...attrs}
        ref={ref}
        className={`min-w-0 flex-1 border-0 bg-transparent p-0 outline-hidden ${TEXT} ${font}`}
      />
      {suffix && <span className="shrink-0 text-sm text-ink-muted">{suffix}</span>}
    </div>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  hint?: string;
}

/** A `pill` select is a value shown as a badge that can be changed in place (the status of a row): no border, and the fill of a `StatusPill` of its `tone` — the caller picks the tone, never a colour. */
const PILL =
  "rounded-full px-2.5 py-0.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50";

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  size?: Size;
  variant?: "field" | "pill";
  /** The tone of a `pill` select (the value it shows); ignored by a `field`. Neutral when omitted. */
  tone?: Tone;
  /** The common case; `children` (`<option>`s) take over when the options are irregular. */
  options?: SelectOption[];
  /** A first, empty option (`value=""`), disabled when the field is `required` so it can't be submitted. */
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = "md", variant = "field", tone = "neutral", options, placeholder, className = "", children, ...rest },
  ref,
) {
  const { attrs } = useControlAttrs(rest);
  return (
    <select
      {...rest}
      {...attrs}
      ref={ref}
      className={
        variant === "pill"
          ? `${PILL} ${TONE_FILL[tone]}${className ? ` ${className}` : ""}`
          : `${CONTROL} ${TEXT} ${SIZE[size]} ${className}`
      }
    >
      {placeholder !== undefined && (
        <option value="" disabled={rest.required}>
          {placeholder}
        </option>
      )}
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
          {opt.hint ? ` — ${opt.hint}` : ""}
        </option>
      ))}
      {children}
    </select>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ mono, className = "", ...rest }, ref) {
    const { attrs } = useControlAttrs(rest);
    return (
      <textarea
        {...rest}
        {...attrs}
        ref={ref}
        className={`${CONTROL} ${TEXT} ${SIZE.md} resize-y ${mono ? "font-mono" : ""} ${className}`}
      />
    );
  },
);

export interface FileInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "value" | "onChange" | "multiple"> {
  /** The chosen file, or `null` when the choice is cleared. Reading it is the caller's business (`file.text()`). */
  onFile: (file: File | null) => void;
}

/**
 * A single-file picker. The native control, so the keyboard, the label and the
 * browser's own "no file chosen" all come for free; only its button is dressed
 * like a secondary button (`file:`), the rest of the line stays plain text.
 * The file never leaves the browser through this control: the caller reads it
 * and sends its content.
 */
export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(function FileInput(
  { onFile, className = "", ...rest },
  ref,
) {
  const { attrs } = useControlAttrs({ ...rest, type: "file" });
  return (
    <input
      {...rest}
      {...attrs}
      ref={ref}
      type="file"
      onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      className={`block w-full ${TEXT} file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-line-field file:bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink file:hover:bg-sunken ${className}`}
    />
  );
});
